import { colors, intl } from '@beelday/common'
import styled from '@emotion/styled'
import { useSelector } from 'app-redux'
import { FocusedFeatureTypes } from 'common/types'
import { selectAudioStreamState } from 'features/audio-streaming/audio-streaming-redux'
import { selectYouTubeStreamState } from 'features/youtube-streaming/youtube-streaming-redux'
import { FC } from 'react'
import { SceneVideoUser } from 'video-conference-media'
import {
	ANSWER_CARD_MAX_WIDTH_PX,
	isAnswerCard,
} from './model/participant-stage'
import { selectFocusedFeature } from './redux'
import { TraineeFocusedFeature } from './trainee-focused-feature'

type Props = {
	streams: SceneVideoUser[]
}

/**
 * The one place a card the participant answers in is bounded and centred: the
 * stage (`InPersonLayout`) centres whatever it holds, so the card only has to
 * be no wider than a comfortable line - and not hold a column beside it for
 * cameras that are not there. A tool's own card then fills this column.
 */
const AnswerColumn = styled.div`
	display: flex;
	flex: 0 1 ${ANSWER_CARD_MAX_WIDTH_PX}px;
	min-width: 0;
`

const Card = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	row-gap: 16px;
	text-align: center;
	width: 100%;
	height: 100%;
	padding: 40px;
`

const Label = styled.div`
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.16em;
	text-transform: uppercase;
	color: ${colors.heather};
`

const Title = styled.h1`
	margin: 0;
	color: ${colors.indigoBlue};
	font-size: 32px;
	font-weight: 500;
	line-height: 1.25;
	max-width: 22ch;
`

const Note = styled.p`
	margin: 0;
	font-size: 16px;
	line-height: 1.6;
	color: ${colors.blackish};
	max-width: 44ch;
`

type PlayingProps = {
	/** What the trainer put on, so the room knows this screen is not stuck. */
	name?: string
	paused: boolean
	titleKey: string
}

const PlayingInTheRoom: FC<PlayingProps> = ({ name, paused, titleKey }) => (
	<Card>
		<Label>
			<intl.Translate>
				{paused ? 'in_person.media.paused' : 'in_person.media.now_playing'}
			</intl.Translate>
		</Label>
		<Title>{name || <intl.Translate>{titleKey}</intl.Translate>}</Title>
		<Note>
			<intl.Translate>in_person.media.description</intl.Translate>
		</Note>
	</Card>
)

const AudioPlayingInTheRoom: FC = () => {
	const audio = useSelector(selectAudioStreamState)
	return (
		<PlayingInTheRoom
			name={audio?.name}
			paused={audio?.state === 'Paused'}
			titleKey="in_person.media.audio.title"
		/>
	)
}

const YouTubePlayingInTheRoom: FC = () => {
	const video = useSelector(selectYouTubeStreamState)
	return (
		<PlayingInTheRoom
			name={video?.name}
			paused={video?.state === 'Paused'}
			titleKey="in_person.media.youtube.title"
		/>
	)
}

/**
 * The whiteboard and the notepad are the projector's alone: the room works on
 * them together on the one screen at the front, so a laptop does not mirror
 * them. Phones show their "presentation in progress" screen for the same.
 */
const BoardInTheRoom: FC<{ titleKey: string }> = ({ titleKey }) => (
	<Card data-testid="in_person.board">
		<Title>
			<intl.Translate>{titleKey}</intl.Translate>
		</Title>
		<Note>
			<intl.Translate>in_person.board.description</intl.Translate>
		</Note>
	</Card>
)

/** The shared stage, with a card to answer in put in the centred column. */
const SharedStage: FC<Props> = ({ streams }) => {
	const type = useSelector(selectFocusedFeature)?.type
	const stage = <TraineeFocusedFeature streams={streams} />

	return type && isAnswerCard(type) ? (
		<AnswerColumn data-testid="in_person.answer_column">{stage}</AnswerColumn>
	) : (
		stage
	)
}

/**
 * The knowledge-check review is the projector's alone: the room follows the
 * trainer through it on one screen, so a laptop does not mirror the correct
 * answers. Before the review the shared screen stays - it shows the
 * participant their score and their own picks, never an answer they missed.
 */
const KnowledgeCheckInTheRoom: FC<Props> = ({ streams }) => {
	const isReview = useSelector(state => state.knowledgeChecks.isReview)

	return isReview ? (
		<Card data-testid="in_person.knowledge_check.review">
			<Title>
				<intl.Translate>in_person.mobile.knowledge_check.review</intl.Translate>
			</Title>
			<Note>
				<intl.Translate>
					in_person.mobile.knowledge_check.review.description
				</intl.Translate>
			</Note>
		</Card>
	) : (
		<SharedStage streams={streams} />
	)
}

/**
 * The participant's stage in-person: the shared one, except that media, the
 * whiteboard, the notepad and the knowledge-check review are on the projector
 * only.
 *
 * `AudioStreamingUser` and `YouTubeStreamingUser` are real synchronised
 * players - wavesurfer and the YouTube iframe API, each seeking to a
 * server-supplied offset. That is right online, where everybody is in a
 * different room wearing their own headphones, and wrong in person, where it
 * would mean every laptop and phone in one room playing the same track a few
 * hundred milliseconds apart. The trainer's machine is the one wired to the
 * speakers, so it is the only one that plays.
 *
 * Participants get a card naming what is on instead of silence, so nobody
 * assumes their screen has frozen. Both players also call `useMicApi` to mute
 * the listener while media runs, and a stationary scheme enables no
 * `MicrophoneType`, so not mounting them avoids that failing call too.
 *
 * Everything else is delegated untouched.
 */
export const TraineeFocusedFeatureInPerson: FC<Props> = ({ streams }) => {
	const focusedFeature = useSelector(selectFocusedFeature)

	switch (focusedFeature?.type) {
		case FocusedFeatureTypes.AUDIO_STREAMING:
			return <AudioPlayingInTheRoom />
		case FocusedFeatureTypes.YOUTUBE_STREAMING:
			return <YouTubePlayingInTheRoom />
		case FocusedFeatureTypes.WHITEBOARD:
			return <BoardInTheRoom titleKey="in_person.board.whiteboard" />
		case FocusedFeatureTypes.NOTEPAD:
			return <BoardInTheRoom titleKey="in_person.board.notepad" />
		case FocusedFeatureTypes.KNOWLEDGE_CHECK:
			return <KnowledgeCheckInTheRoom streams={streams} />
		default:
			return <SharedStage streams={streams} />
	}
}

export default TraineeFocusedFeatureInPerson
